import React from "react";
import { Stack, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip from "@hyro-components/Tooltip";
import { formatPercentValue } from "@utils";

const TrendIndicator = ({ value, previousValue, periodInDays = "30", showPeriod = true, tooltipText, invertTrend = false }) => {
  const theme = useTheme();

  if (previousValue == null || previousValue === 0) {
    return null;
  }

  const percentageChange = ((value - previousValue) / previousValue) * 100;
  const isPositive = percentageChange >= 0;
  const isGood = invertTrend ? !isPositive : isPositive;
  const displayPercentage = formatPercentValue(Math.abs(percentageChange));

  const pill = (
    <DeltaPill data-testid="delta-pill" $isGood={isGood}>
      <ArrowIcon color={isGood ? theme.palette.success[500] : theme.palette.error[400]} isPositive={isPositive} />
      <DeltaText $isGood={isGood}>{displayPercentage}</DeltaText>
    </DeltaPill>
  );

  return (
    <Stack direction="row" alignItems="center" gap="6px">
      {tooltipText
        ? <Tooltip testId="trend-indicator" title={tooltipText} popperStyle={{ marginBottom: "8px" }}>{pill}</Tooltip>
        : pill}
      {showPeriod && <PeriodText>{`Last ${periodInDays} days`}</PeriodText>}
    </Stack>
  );
};

const ArrowIcon = ({ color, isPositive }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6.35355 1.64645C6.15829 1.45118 5.84171 1.45118 5.64645 1.64645L2.64645 4.64645C2.45118 4.84171 2.45118 5.15829 2.64645 5.35355C2.84171 5.54882 3.15829 5.54882 3.35355 5.35355L5.5 3.20711L5.5 10C5.5 10.2761 5.72386 10.5 6 10.5C6.27614 10.5 6.5 10.2761 6.5 10L6.5 3.20711L8.64645 5.35355C8.84171 5.54882 9.15829 5.54882 9.35355 5.35355C9.54882 5.15829 9.54882 4.84171 9.35355 4.64645L6.35355 1.64645Z"
      transform={isPositive ? undefined : "rotate(180 6 6)"}
      stroke={color}
      fill={color}
      strokeWidth="0.2"
    />
  </svg>
);

const DeltaPill = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "$isGood"
})(({ theme, $isGood }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: "2px",
  padding: "0 4px",
  borderRadius: "4px",
  backgroundColor: $isGood ? theme.palette.success[200] : theme.palette.error[100]
}));

const DeltaText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$isGood"
})(({ theme, $isGood }) => ({
  color: $isGood ? theme.palette.success[500] : theme.palette.error[400],
  ...theme.typography.caption
}));

const PeriodText = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  lineHeight: "18px",
  color: theme.palette.text[700]
}));

export default TrendIndicator;
